import React from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  Zoom,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded';
import EmailIcon from '@mui/icons-material/Email';
import { useTheme } from '@mui/material/styles';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

/**
 * Configuración visual por tipo de cartel.
 * Se conecta con los tokens de color del tema MUI.
 */
const getVariantConfig = (type, theme) => {
  switch (type) {
    case 'error':
      return {
        mainColor: theme.palette.error.main,
        bgLight: 'rgba(226, 70, 76, 0.10)',
        borderRing: theme.palette.error.main,
        IconComponent: CloseRoundedIcon,
        defaultConfirmText: 'Entendido',
        confirmButtonColor: theme.palette.error.main,
        ringBadge: false,
      };
    case 'warning':
      return {
        mainColor: '#F59E0B',
        bgLight: '#FFFBEB',
        ringColor: 'rgba(254, 243, 199, 0.65)',
        borderRing: '#F59E0B',
        IconComponent: PriorityHighRoundedIcon,
        defaultConfirmText: 'Aceptar',
        confirmButtonColor: theme.palette.warning.main,
        ringBadge: false,
      };
    case 'delete':
      return {
        mainColor: theme.palette.error.main,
        bgLight: '#FEF2F2',
        ringColor: 'rgba(254, 242, 242, 0.7)',
        borderRing: theme.palette.error.main,
        IconComponent: DeleteOutlineRoundedIcon,
        defaultConfirmText: 'ELIMINAR',
        defaultCancelText: 'CANCELAR',
        confirmButtonColor: theme.palette.error.main,
        ringBadge: true,
      };
    case 'info':
      return {
        mainColor: theme.palette.info.main,
        bgLight: 'rgba(28, 124, 213, 0.10)',
        borderRing: theme.palette.info.main,
        IconComponent: InfoOutlinedIcon,
        defaultConfirmText: 'Aceptar',
        confirmButtonColor: theme.palette.info.main,
        ringBadge: false,
      };
    case 'question':
      return {
        mainColor: '#00A651',
        bgLight: 'rgba(236, 253, 245, 0.85)',
        ringColor: 'rgba(209, 250, 229, 0.75)',
        borderRing: '#00A651',
        IconComponent: QuestionMarkRoundedIcon,
        defaultConfirmText: 'Aceptar',
        confirmButtonColor: '#00A651',
        ringBadge: true,
      };
    case 'success':
    default:
      return {
        mainColor: '#00A651',
        bgLight: 'rgba(236, 253, 245, 0.85)',
        borderRing: 'rgba(209, 250, 229, 0.75)',
        IconComponent: CheckRoundedIcon,
        defaultConfirmText: 'Aceptar',
        confirmButtonColor: '#00A651',
        ringBadge: false,
      };
  }
};

/**
 * FeedbackModal
 * Componente de presentación puro para carteles centrales de la aplicación.
 */
const FeedbackModal = ({
  open = false,
  type = 'success',
  icon = null,
  title = '',
  description = '',
  detail = null, // string | JSX | { text, highlight, suffix, subtext }
  confirmText,
  cancelText,
  showCancel = false,
  stackedButtons = false,
  confirmButtonColor,
  confirmButtonHoverColor,
  confirmButtonActiveColor,
  ringBadge = undefined,
  maxWidth,
  centerButton = false,
  notificationBanner = null,
  onConfirm,
  onCancel,
  disableBackdropClick = false,
}) => {
  const theme = useTheme();
  const config = getVariantConfig(type, theme);
  const IconComponent = icon || config.IconComponent;

  const handleClose = (event, reason) => {
    if (disableBackdropClick && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    if (onCancel) {
      onCancel();
    }
  };

  const resolvedConfirmText = confirmText || config.defaultConfirmText;
  const resolvedCancelText = cancelText || config.defaultCancelText || 'Cancelar';
  const isStacked = stackedButtons || type === 'delete';

  // Renderizado del bloque de detalle secundario
  const renderDetailContent = () => {
    if (!detail) return null;

    if (typeof detail === 'string') {
      return (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.6,
            textAlign: 'center',
            fontSize: '0.92rem',
          }}
        >
          {detail}
        </Typography>
      );
    }

    if (typeof detail === 'object' && React.isValidElement(detail)) {
      return detail;
    }

    if (typeof detail === 'object') {
      const { text, highlight, suffix, subtext } = detail;
      return (
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: type === 'delete' ? '#334155' : 'text.secondary',
              lineHeight: 1.55,
              textAlign: 'center',
              fontSize: '0.92rem',
              mb: subtext ? 0.75 : 0,
            }}
          >
            {text && `${text} `}
            {highlight && (
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: type === 'delete' ? '#0F172A' : 'primary.main',
                  wordBreak: 'break-word',
                }}
              >
                {highlight}
              </Box>
            )}
            {suffix && ` ${suffix}`}
          </Typography>
          {subtext && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#64748B',
                fontSize: '0.82rem',
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {subtext}
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  const isRing = ringBadge !== undefined ? ringBadge : config.ringBadge;
  const resolvedConfirmButtonColor = confirmButtonColor || config.confirmButtonColor;
  const isGreen =
    resolvedConfirmButtonColor === '#00a651' ||
    resolvedConfirmButtonColor === '#00A651' ||
    resolvedConfirmButtonColor === '#2e7d32' ||
    resolvedConfirmButtonColor === '#32A430';

  const resolvedConfirmHoverColor =
    confirmButtonHoverColor ||
    (isGreen
      ? '#008f45'
      : type === 'delete'
        ? '#c62828'
        : theme.palette.custom?.CBA_Blue_Dark || '#004582');

  const resolvedConfirmActiveColor =
    confirmButtonActiveColor || (isGreen ? '#007a3b' : undefined);

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted={false}
      onClose={handleClose}
      aria-labelledby="feedback-dialog-title"
      aria-describedby="feedback-dialog-description"
      sx={{
        zIndex: 9999,
        '& .MuiDialog-container': {
          zIndex: 9999,
          py: { xs: 2, sm: 3 },
        },
      }}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: maxWidth || (notificationBanner ? '480px' : '390px'),
          borderRadius: '24px',
          p: { xs: 2.5, sm: 3.5 },
          pt: { xs: 3.5, sm: 4.5 },
          boxShadow: '0 8px 16px -2px rgba(15, 23, 42, 0.06), 0 24px 48px -8px rgba(15, 23, 42, 0.16)',
          backgroundColor: '#FFFFFF',
          overflow: 'visible',
          m: 2,
          maxHeight: 'calc(100vh - 48px)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(15, 23, 42, 0.55)',
          backdropFilter: 'blur(6px)',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          pt: { xs: 1, sm: 1.5 },
          overflow: 'visible',
          overflowY: 'visible',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          overflow: 'visible',
        }}
      >
        {/* Status Icon */}
        {isRing ? (
          type === 'warning' ? (
            <Box
              sx={{
                width: 76,
                height: 76,
                borderRadius: '50%',
                backgroundColor: '#FFFBEB',
                boxShadow: '0 0 0 8px rgba(254, 243, 199, 0.65)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 0.5,
                mb: 2.5,
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{
                  width: 38,
                  height: 38,
                  color: '#F59E0B',
                  fill: 'none',
                  stroke: '#F59E0B',
                  strokeWidth: 2.5,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                }}
              >
                <circle cx="12" cy="12" r="9" stroke="#FBBF24" opacity="0.35" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </Box>
            </Box>
          ) : type === 'question' ? (
            <Box
              sx={{
                width: 76,
                height: 76,
                borderRadius: '50%',
                backgroundColor: '#ECFDF5',
                boxShadow: '0 0 0 8px rgba(209, 250, 229, 0.75)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
              }}
            >
              <QuestionMarkRoundedIcon
                sx={{
                  fontSize: 42,
                  color: '#00A651',
                  stroke: '#00A651',
                  strokeWidth: 0.5,
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: config.bgLight,
                boxShadow: `0 0 0 8px ${config.ringColor || 'rgba(254, 242, 242, 0.7)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 0.5,
                mb: 2.5,
              }}
            >
              <IconComponent
                sx={{
                  fontSize: 34,
                  color: config.mainColor,
                }}
              />
            </Box>
          )
        ) : type === 'question' ? (
          <Box
            sx={{
              width: { xs: 84, sm: 96 },
              height: { xs: 84, sm: 96 },
              borderRadius: '50%',
              backgroundColor: 'rgba(236, 253, 245, 0.85)',
              border: '4px solid rgba(209, 250, 229, 0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2.5,
              boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
            }}
          >
            <QuestionMarkRoundedIcon
              sx={{
                fontSize: { xs: 46, sm: 54 },
                color: '#00A651',
                stroke: '#00A651',
                strokeWidth: 0.5,
              }}
            />
          </Box>
        ) : type === 'success' ? (
          <Box
            sx={{
              width: { xs: 84, sm: 96 },
              height: { xs: 84, sm: 96 },
              borderRadius: '50%',
              backgroundColor: 'rgba(236, 253, 245, 0.85)',
              border: '4px solid rgba(209, 250, 229, 0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 0.5,
              mb: 2.5,
              boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
            }}
          >
            <CheckRoundedIcon
              sx={{
                fontSize: { xs: 46, sm: 54 },
                color: '#00A651',
                stroke: '#00A651',
                strokeWidth: 1.5,
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: '74px',
              height: '74px',
              borderRadius: '50%',
              backgroundColor: config.bgLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 0.5,
              mb: 2.5,
            }}
          >
            <Box
              sx={{
                width: '54px',
                height: '54px',
                borderRadius: '50%',
                backgroundColor: '#FFFFFF',
                border: `2px solid ${config.borderRing}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
              }}
            >
              <IconComponent
                sx={{
                  fontSize: 32,
                  color: config.mainColor,
                  fontWeight: 700,
                }}
              />
            </Box>
          </Box>
        )}

        {/* Headline */}
        {title && (
          <Typography
            id="feedback-dialog-title"
            variant="h2"
            sx={{
              fontFamily: theme.typography.h2.fontFamily,
              fontWeight: 800,
              fontSize: { xs: '1.25rem', sm: '1.4rem' },
              lineHeight: 1.3,
              letterSpacing: '-0.02em',
              color: '#0F172A',
              mb: description || detail || notificationBanner ? 2 : 3,
            }}
          >
            {title}
          </Typography>
        )}

        {/* Descripción general opcional */}
        {description && (
          <Typography
            id="feedback-dialog-description"
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: '0.94rem',
              lineHeight: 1.5,
              mb: detail || notificationBanner ? 2 : 3,
              px: 0.5,
            }}
          >
            {description}
          </Typography>
        )}

        {/* Notification Banner */}
        {notificationBanner && notificationBanner.email && (
          <Box
            component="section"
            sx={{
              width: '100%',
              backgroundColor: '#F0FDF4',
              border: '1px solid #DCFCE7',
              borderRadius: '14px',
              pl: { xs: 2, sm: 2.25 },
              pr: { xs: 2, sm: 2.25 },
              py: { xs: 1.75, sm: 2 },
              mb: 3.5,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              textAlign: 'left',
              gap: 1.5,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: '#00A651',
                borderTopLeftRadius: '6px',
                borderBottomLeftRadius: '6px',
              }}
            />
            <EmailIcon sx={{ color: '#00A651', fontSize: 22, flexShrink: 0, mt: { xs: 0.25, sm: 0 }, ml: 0.5 }} />
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.82rem', sm: '0.88rem' },
                color: '#334155',
                lineHeight: 1.55,
              }}
            >
              <span>Notificación enviada a: </span>
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: '#0F172A',
                  wordBreak: 'break-all',
                }}
              >
                {notificationBanner.email}
              </Box>
              <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                {' '}{notificationBanner.message || 'informando la autorización de los cursos.'}
              </Box>
            </Typography>
          </Box>
        )}

        {/* Secondary Information Box (Detail Card) */}
        {detail && (
          <Box
            sx={{
              width: '100%',
              backgroundColor: type === 'delete' ? '#F8FAFC' : 'rgba(0, 154, 218, 0.04)',
              border: type === 'delete' ? '1px solid #E2E8F0' : '1px solid rgba(0, 154, 218, 0.14)',
              borderRadius: '16px',
              p: { xs: 2, sm: 2.25 },
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {renderDetailContent()}
          </Box>
        )}

        {/* Botones de acción */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: isStacked ? 'column' : (showCancel ? 'row' : 'column'),
            gap: 1.5,
            justifyContent: centerButton && !showCancel ? 'center' : 'stretch',
            alignItems: centerButton && !showCancel ? 'center' : 'stretch',
          }}
        >
          {/* Botón cancelar si NO están apilados (layout horizontal) */}
          {!isStacked && showCancel && (
            <Button
              variant="outlined"
              fullWidth
              onClick={onCancel}
              sx={{
                flex: 1,
                minHeight: '48px',
                borderRadius: '12px',
                fontWeight: 700,
                fontSize: '0.875rem',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                borderColor: '#E2E8F0',
                backgroundColor: '#FFFFFF',
                color: '#475569',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                '&:hover': {
                  borderColor: '#CBD5E1',
                  backgroundColor: '#F8FAFC',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              {resolvedCancelText}
            </Button>
          )}

          {/* Botón de Confirmación Principal */}
          <Button
            variant="contained"
            fullWidth={!(centerButton && !showCancel)}
            onClick={onConfirm}
            autoFocus
            sx={{
              flex: !isStacked && showCancel ? 1 : undefined,
              width: centerButton && !showCancel ? { xs: '100%', sm: '210px' } : undefined,
              minHeight: '48px',
              borderRadius: '12px',
              fontWeight: 700,
              fontSize: '0.875rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              backgroundColor: resolvedConfirmButtonColor,
              boxShadow: isGreen
                ? '0 4px 14px rgba(0, 166, 81, 0.30)'
                : type === 'delete'
                  ? '0 4px 14px rgba(226, 70, 76, 0.30)'
                  : '0 4px 14px rgba(0, 154, 218, 0.32)',
              '&:hover': {
                backgroundColor: resolvedConfirmHoverColor,
                boxShadow: isGreen
                  ? '0 6px 18px rgba(0, 166, 81, 0.40)'
                  : type === 'delete'
                    ? '0 6px 18px rgba(226, 70, 76, 0.45)'
                    : '0 6px 18px rgba(0, 154, 218, 0.42)',
              },
              '&:active': {
                transform: 'scale(0.98)',
                backgroundColor: resolvedConfirmActiveColor || resolvedConfirmHoverColor,
              },
            }}
          >
            {resolvedConfirmText}
          </Button>

          {/* Botón cancelar si SÍ están apilados (debajo del botón principal) */}
          {isStacked && showCancel && (
            <Button
              variant="outlined"
              fullWidth
              onClick={onCancel}
              sx={{
                minHeight: '48px',
                borderRadius: '12px',
                fontWeight: 700,
                fontSize: '0.875rem',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                borderColor: '#E2E8F0',
                backgroundColor: '#FFFFFF',
                color: '#64748B',
                '&:hover': {
                  borderColor: '#CBD5E1',
                  backgroundColor: '#F8FAFC',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              {resolvedCancelText}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
